import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * buttonVariants — single source of truth for all button styles.
 *
 * Variants
 * ─────────
 *  primary      Main CTA. bg-sossiety-red, white text.
 *  outline      Secondary action on dark bg. Transparent + white/20 border.
 *  outline-light  Secondary action on light/white bg (modals). Black border, black text.
 *  ghost        Low-emphasis. Transparent, white/50 text.
 *  destructive  Destructive confirm. Red-tinted bg + red text + red border.
 *  pill         Inline status label. Very subtle red surface.
 *  success      Confirm / yes action. Green, semantic (not brand red).
 *  default      Shadcn compat alias → same as primary.
 *  secondary    Shadcn compat. Subtle white surface.
 *  link         Shadcn compat.
 *
 * Sizes
 * ─────
 *  xs      h-6  px-3 text-xs       — inline card buttons (Yes/No, Add Notes)
 *  sm      h-8  px-3 text-xs       — inline pills, card footers
 *  md      h-10 px-4 text-sm       — default
 *  lg      h-12 px-6 text-sm bold  — full-width CTAs
 *  icon    w-9  h-9               — icon-only (pair with IconButton)
 *  default Shadcn compat alias → same as md
 *
 * Shape
 * ─────
 *  rounded (default) rounded-full — standalone CTAs, inline actions
 *  squared           rounded-xl   — inside modal dialogs / card containers
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sossiety-red/50 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // ── Brand variants ──────────────────────────────────────────────────
        primary:
          "bg-sossiety-red text-white hover:bg-sossiety-red-hover active:bg-sossiety-red-hover",
        outline:
          "border border-white text-white hover:bg-white/10",
        "outline-light":
          "border border-black text-black bg-transparent hover:bg-black/5",
        ghost:
          "text-white/50 hover:text-white",
        destructive:
          "bg-sossiety-red/20 text-sossiety-red border border-sossiety-red/30 hover:bg-sossiety-red/30",
        pill:
          "bg-sossiety-red/[0.08] text-sossiety-red border border-sossiety-red/25 hover:bg-sossiety-red/[0.15]",
        success:
          "bg-green-600 text-white hover:bg-green-700 active:bg-green-700",

        // ── Shadcn compat aliases (do not use in new code) ──────────────────
        default:
          "bg-sossiety-red text-white hover:bg-sossiety-red-hover",
        secondary:
          "bg-white/10 text-white hover:bg-white/20",
        link:
          "text-sossiety-red underline-offset-4 hover:underline",
      },
      size: {
        // ── Brand sizes ─────────────────────────────────────────────────────
        xs:   "h-6  px-3 text-xs",
        sm:   "h-8  px-3 text-xs",
        md:   "h-10 px-4 text-sm",
        lg:   "h-12 px-6 text-sm font-semibold",
        icon: "w-9 h-9",

        // ── Shadcn compat alias ──────────────────────────────────────────────
        default: "h-10 px-4 text-sm",
      },
      shape: {
        rounded: "rounded-full",
        squared: "rounded-xl",
      },
    },
    defaultVariants: {
      variant: "primary",
      size:    "md",
      shape:   "rounded",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, shape, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
